import $ from 'jquery'
import _ from 'lodash'
import moment from 'moment'
import { BACKEND_URL } from '../config'
import { dateToISO } from '../common/utils'
import Icons from '../controllers/icons'
import Model from './model'
import TrackedGeolocation from './tracked-geolocation'
import GeolocationTracker from './geolocation-tracker'
import Person from './person'
import Filter from './filter'
import TransferRelatedMessage from './transfer-related-message'
import StepDelayNotification from './notifications/step-delay-notification'
import StepDoneNotification from './notifications/step-done-notification'
import Step from './step'

const STATUS = {
  LOADING: 'Loading',
  SCHEDULED: 'Scheduled',
  TRAVELING: 'Traveling',
  ARRIVED: 'Arrived',
  UNLOADING: 'Unloading'
}
const SENNDER_ORANGE = '#ff7800'
const GRAY = '#808080'

class Transfer extends Model {
  static get url() {
    return `${BACKEND_URL}/infrastructure/transfers`
  }

  static get parsers() {
    return {
      ...Model.parsers,
      steps: dataArray => Step.many(dataArray),
      messages: dataArray => TransferRelatedMessage.many(dataArray, 'timestamp'),
      locations: dataArray => TrackedGeolocation.many(dataArray, 'time'),
      filters: dataArray => Filter.many(dataArray, 'id'),
      trackers: dataArray => GeolocationTracker.many(dataArray),
      driver: dataDict => Person.one(dataDict),
      startTime: timeString => moment(timeString)
    }
  }

  static async generateWaybill(id) {
    const waybillUrl = `${BACKEND_URL}/infrastructure/transfers/${id}/waybill/`
    return new Promise((resolve, reject) => {
      $.get({
        url: waybillUrl,
        dataType: 'json'
      })
        .done(resolve)
        .fail(reject)
    })
  }

  get oldestMessage() {
    return _.first(this.messages)
  }

  get messagesCautionLabel() {
    if (this.messages.find(message => message.cautionLabel === 'caution'))
      return 'caution'
    return ''
  }

  get latestCompletedStep() {
    const completedSteps = this.steps.filter(step => step.isDone)
    return _.last(_.values(completedSteps))
  }

  get orderGroup() {
    return this.order ? this.order.orderGroup : undefined
  }

  get carrierCompany() {
    return this.order ? this.order.carrierCompany : undefined
  }

  get carrierContact() {
    return this.order ? this.order.carrierContact : undefined
  }

  get customerCompany() {
    return this.orderGroup ? this.orderGroup.customerCompany : undefined
  }

  get customerContact() {
    return this.orderGroup ? this.orderGroup.customerContact : undefined
  }

  get gpsIsOnline() {
    return _.filter(this.trackers, { isOnline: true }).length > 0
  }

  get departures() {
    return _.filter(this.steps, { type: 'DEPARTURE' })
  }

  get departure() {
    return _.first(this.departures)
  }

  get origin() {
    if (this.departure) return this.departure.hub
    return null
  }

  get arrivals() {
    return _.filter(this.steps, { type: 'ARRIVAL' })
  }

  get arrival() {
    return _.last(this.arrivals)
  }

  get nextStep() {
    return _.find(this.steps, { isDone: false })
  }

  get hubs() {
    return _.uniqBy(_.map(this.steps, 'hub'), 'id')
  }

  get isLoading() {
    const firstArrival = _.first(this.arrivals)
    if (
      firstArrival &&
      this.departure &&
      moment(firstArrival.scheduledTime).isSameOrBefore(this.departure.scheduledTime)
    )
      return firstArrival.isDone && !this.departure.isDone
    return false
  }

  get isUnloading() {
    const lastDeparture = _.last(this.departures)
    if (
      this.arrival &&
      moment(lastDeparture.scheduledTime).isSameOrAfter(this.arrival.scheduledTime)
    )
      return this.arrival && this.arrival.isDone && !lastDeparture.isDone
    return false
  }

  get markers() {
    return this.steps.filter(s => ['GEOFENCE', 'DEPARTURE'].includes(s.type))
  }

  get destination() {
    return this.arrival.hub
  }

  get pictureSteps() {
    return _.filter(this.steps, { type: 'PICTURESTEP' })
  }

  get date() {
    return dateToISO(this.startTime)
  }

  get lastLocation() {
    return _.last(this.locations)
  }

  get lastCoordinate() {
    return this.lastLocation ? this.lastLocation.coordinates : undefined
  }

  get lastUpdate() {
    if (this.lastLocation) return moment(this.lastLocation.time)
    return undefined
  }

  get delayStatus() {
    if (this.status === 'ARRIVED' && this.arrival) return this.arrival.delayStatus
    return this.nextStep.delayStatus
  }

  get delayHuman() {
    if (this.status === 'ARRIVED' && this.arrival) return this.arrival.delayHuman
    return this.nextStep && this.nextStep.delayHuman
  }

  get status() {
    if (!(this.departure && this.departure.isDone) && !this.isLoading) return 'SCHEDULED'
    if (this.isLoading) return 'LOADING'
    if (
      this.departure &&
      this.departure.isDone &&
      (!this.arrival || !this.arrival.isDone)
    )
      return 'TRAVELING'
    if (this.isUnloading) return 'UNLOADING'
    if (this.arrival && this.arrival.isDone && !this.isUnloading) return 'ARRIVED'
    return 'SCHEDULED'
  }

  get shortStatusHuman() {
    if (this.status === 'TRAVELING') return `${this.nextStep.name}:`
    if (this.status === 'ARRIVED') return `${STATUS.ARRIVED}:`
    return STATUS[this.status]
  }

  get statusHuman() {
    if (this.status === 'TRAVELING') return `${this.nextStep.name}: ${this.delayHuman}`
    if (this.status === 'ARRIVED') return `${STATUS.ARRIVED}: ${this.delayHuman}`
    return STATUS[this.status]
  }

  get statusLabel() {
    if (this.status === 'SCHEDULED' || this.status === 'ARRIVED') return ''
    return this.nextStep.delayLabel
  }

  get isOngoing() {
    return !(this.status === 'ARRIVED' || this.status === 'SCHEDULED')
  }

  get trajectory() {
    // locations from GPS
    const vectorCoordinates = (this.locations || []).map(
      location => location.coordinates.coordinates
    )

    if (this.departure) {
      // add origin point
      vectorCoordinates.unshift([
        this.departure.hub.address.longitude,
        this.departure.hub.address.latitude
      ])
    }

    // add destination point if arrived
    if (this.status === 'ARRIVED' && this.arrival) {
      vectorCoordinates.push([
        this.arrival.hub.address.longitude,
        this.arrival.hub.address.latitude
      ])
    }

    return [
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: vectorCoordinates
        },
        properties: {}
      }
    ]
  }

  get hubLinks() {
    const addresses = _.uniqBy(
      _.compact(_.map(this.hubs, 'address')),
      address => address.locality
    )
    const hubLocations = addresses.map(address => [address.latitude, address.longitude])

    return L.polyline(hubLocations, {
      color: 'grey',
      weight: 1.5,
      dashArray: '5, 5'
    })
  }

  get markerIcon() {
    if (this.status === 'ARRIVED') return Icons.markerDeactivated
    return Icons.marker
  }

  get layers() {
    const style = {
      color: this.isOngoing ? SENNDER_ORANGE : GRAY,
      opacity: 1
    }

    const layers = this.trajectory.map(t => L.geoJson(t.geometry, { style }))
    layers.push(this.hubLinks)

    let stopN = 0
    this.markers.forEach(marker => {
      stopN += 1
      if (marker.type === 'DEPARTURE') {
        layers.push(
          L.marker([marker.hub.address.latitude, marker.hub.address.longitude], {
            icon: this.markerIcon
          }).bindPopup(`${stopN} - ${marker.hub.string}`)
        )
      } else if (marker.type === 'GEOFENCE') {
        var coordinates = [marker.center[1], marker.center[0]]
        layers.push(
          L.marker(coordinates, { icon: this.markerIcon }).bindPopup(
            `${stopN} - ${marker.name}`
          )
        )
        var options = { radius: marker.radius }
        if (marker.isDone) {
          options.color = SENNDER_ORANGE
          options.filColor = SENNDER_ORANGE
        }
        layers.push(L.circle(coordinates, options))
      }
    })

    if (this.arrival) {
      layers.push(
        L.marker(
          [this.arrival.hub.address.latitude, this.arrival.hub.address.longitude],
          {
            icon: this.markerIcon
          }
        ).bindPopup(`${stopN} - ${this.arrival.hub.string}`)
      )
    }

    if (
      this.status === 'TRAVELING' &&
      this.lastCoordinate &&
      this.lastCoordinate.coordinates
    ) {
      layers.push(
        L.marker(
          [this.lastCoordinate.coordinates[1], this.lastCoordinate.coordinates[0]],
          {
            icon: Icons.sennderMarker
          }
        )
      )
    }

    return layers
  }

  buildNotification(filter) {
    let notification
    if (filter.eventId === 'STEP_DELAY_EXPECTED') {
      notification = new StepDelayNotification(this, filter)
    } else if (filter.eventId === 'STEP_DONE') {
      notification = new StepDoneNotification(this, filter)
    }
    if (notification && notification.isFrontendCompatible) return notification
  }
}

export default Transfer
